/** Small presentation helpers for the message list and reader. */
import type { Envelope, MailAddress, RemoteContentDefault } from "../shared/types";

/**
 * Pure predicate driving the Reader's "Remote content was blocked" banner.
 * Extracted from the JSX condition so it is testable without a DOM render
 * (Extract-Before-Mock). The banner shows ONLY when the message actually
 * embeds remote content, the user has NOT already revealed it, and the account
 * default is not `always` (an `always` account never needs the prompt).
 */
export function shouldShowRemoteBanner(
  hasRemoteContent: boolean,
  showRemote: boolean,
  remoteContentDefault: RemoteContentDefault,
): boolean {
  return hasRemoteContent && !showRemote && remoteContentDefault !== "always";
}

/**
 * Extract the registrable domain (eTLD+1) from a hostname.
 * mail.anthropic.com → anthropic.com
 * github.com → github.com
 * Works for common 2-part TLDs (co.uk, com.br, etc.) heuristically.
 */
function rootDomain(hostname: string): string {
  const parts = hostname.split(".");
  if (parts.length <= 2) return hostname;
  // Detect known 2-part TLDs like co.uk, com.br, org.ar, net.au …
  const twoPartTld = /^(co|com|net|org|gov|edu|ac|or|ne)\.[a-z]{2}$/i;
  if (parts.length >= 3 && twoPartTld.test(parts.slice(-2).join("."))) {
    return parts.slice(-3).join(".");
  }
  return parts.slice(-2).join(".");
}

/** Build the proxied favicon URL for a sender domain extracted from an email address.
 *  Always resolves to the registrable root domain so subdomains (mail.*, no-reply.*)
 *  map to the same favicon as the main brand. */
export function faviconUrl(emailAddress: string): string | null {
  if (!emailAddress) return null;
  const at = emailAddress.lastIndexOf("@");
  if (at < 0) return null;
  const raw = emailAddress.slice(at + 1).toLowerCase().trim();
  if (!raw || !raw.includes(".")) return null;
  const domain = rootDomain(raw);
  return `/api/favicon?domain=${encodeURIComponent(domain)}`;
}

export function displayName(addr?: MailAddress): string {
  if (!addr) return "Unknown";
  return addr.name?.trim() || addr.address || "Unknown";
}

export function senderLabel(env: Envelope): string {
  return displayName(env.from[0]);
}

export function initials(label: string): string {
  const parts = label.replace(/[<>"]/g, "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const AVATAR_COLORS = [
  "#3B82F6", // accent-anchored blue first
  "#06B6D4", // cyan
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // red
  "#14B8A6", // teal
];

export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Compact list date: time today, "Yesterday", weekday this week, else date. */
export function formatListDate(iso: string, locale?: string, yesterday = "Yesterday"): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    // Spanish-speaking locales read 24-hour time ("14:05"), not "02:05 p. m.".
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: locale?.startsWith("es") ? false : undefined });
  }
  const diffDays = Math.floor((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 1) return yesterday;
  if (diffDays > 1 && diffDays < 7) return d.toLocaleDateString(locale, { weekday: "short" });
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(
    locale,
    sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" },
  );
}

/** Full, unambiguous date for tooltips and the reader header. */
export function formatFullDate(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: locale?.startsWith("es") ? false : undefined,
  });
}

export type DateGroup = "today" | "yesterday" | "week" | "month" | "older";

/**
 * Bucket a date for the list's section headers. "older" groups by calendar
 * month, so the caller also gets a month key to label it.
 */
export function dateGroup(iso: string, now = new Date()): { group: DateGroup; key: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { group: "older", key: "older:unknown" };
  const days = Math.floor((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (days <= 0) return { group: "today", key: "today" };
  if (days === 1) return { group: "yesterday", key: "yesterday" };
  if (days < 7) return { group: "week", key: "week" };
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) return { group: "month", key: "month" };
  return { group: "older", key: `older:${d.getFullYear()}-${d.getMonth()}` };
}

export function monthLabel(iso: string, locale?: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const s = d.toLocaleDateString(locale, sameYear ? { month: "long" } : { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatBytes(n: number): string {
  if (!n) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i] ?? "B"}`;
}

export type FileKind = "image" | "pdf" | "doc" | "sheet" | "slides" | "archive" | "audio" | "video" | "mail" | "calendar" | "code" | "file";

/** Coarse file family used to pick an attachment icon and tint. */
export function fileKind(contentType: string, filename: string): FileKind {
  const ct = contentType.toLowerCase();
  const ext = filename.toLowerCase().split(".").pop() ?? "";
  if (ct.startsWith("image/")) return "image";
  if (ct === "application/pdf" || ext === "pdf") return "pdf";
  if (ct.startsWith("audio/")) return "audio";
  if (ct.startsWith("video/")) return "video";
  if (ct === "message/rfc822" || ext === "eml") return "mail";
  if (ct === "text/calendar" || ext === "ics") return "calendar";
  if (/(zip|rar|7z|tar|gzip|compressed)/.test(ct) || ["zip", "rar", "7z", "gz", "tgz"].includes(ext)) return "archive";
  if (/(sheet|excel|csv)/.test(ct) || ["xls", "xlsx", "csv", "ods"].includes(ext)) return "sheet";
  if (/(presentation|powerpoint)/.test(ct) || ["ppt", "pptx", "odp", "key"].includes(ext)) return "slides";
  if (/(word|document|rtf|opendocument\.text)/.test(ct) || ["doc", "docx", "odt", "rtf", "txt", "md"].includes(ext)) return "doc";
  if (["json", "xml", "html", "js", "ts", "py", "sh", "yml", "yaml"].includes(ext)) return "code";
  return "file";
}

/** Split plain text into text and link segments (http/https/mailto only). */
export function linkify(text: string): Array<{ text: string; href?: string }> {
  const out: Array<{ text: string; href?: string }> = [];
  const re = /\b(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]]|mailto:[^\s<>"']+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push({ text: text.slice(last, idx) });
    const v = m[0];
    out.push({ text: v, href: /^(https?:|mailto:)/i.test(v) ? v : `mailto:${v}` });
    last = idx + v.length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

/** One recipient in an address field: "Name <addr>" or a bare address. */
export interface Recipient {
  raw: string;
  name: string;
  address: string;
  valid: boolean;
}

const ADDR_RE = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

export function parseRecipient(raw: string): Recipient {
  const s = raw.trim();
  const m = s.match(/^(.*)<([^>]+)>\s*$/);
  const address = (m ? m[2]! : s).trim();
  const name = m ? m[1]!.trim().replace(/^"|"$/g, "") : "";
  return { raw: s, name, address, valid: ADDR_RE.test(address) };
}

/** Split an address field into recipients. Commas inside quotes are respected. */
export function splitRecipients(value: string): Recipient[] {
  const parts: string[] = [];
  let cur = "";
  let quoted = false;
  for (const ch of value) {
    if (ch === '"') quoted = !quoted;
    if ((ch === "," || ch === ";") && !quoted) {
      parts.push(cur);
      cur = "";
    } else cur += ch;
  }
  parts.push(cur);
  return parts.map((p) => p.trim()).filter(Boolean).map(parseRecipient);
}

export function joinRecipients(list: Recipient[]): string {
  return list.map((r) => r.raw).join(", ");
}

const SIG_ALLOWED = new Set(["B", "STRONG", "I", "EM", "U", "S", "A", "BR", "P", "DIV", "SPAN", "UL", "OL", "LI", "IMG", "SMALL", "FONT", "HR"]);

/**
 * Minimal allow-list sanitizer for the user's own signature before it is put
 * into the composer's contenteditable. Drops scripts, event handlers and
 * non-http(s)/mailto links. Without a DOM (tests) it falls back to escaping.
 */
export function sanitizeSignature(html: string): string {
  if (typeof DOMParser === "undefined") {
    return html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild as HTMLElement;
  const walk = (el: Element): void => {
    for (const child of [...el.children]) {
      if (!SIG_ALLOWED.has(child.tagName)) {
        if (["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "TEMPLATE"].includes(child.tagName)) child.remove();
        else child.replaceWith(...child.childNodes);
        continue;
      }
      for (const attr of [...child.attributes]) {
        const n = attr.name.toLowerCase();
        const v = attr.value.trim();
        const okHref = n === "href" && /^(https?:|mailto:)/i.test(v);
        const okSrc = n === "src" && /^(https:|data:image\/(png|jpe?g|gif|webp);)/i.test(v);
        const okStyle = n === "style" && !/url\(|expression|javascript:/i.test(v);
        const okPlain = ["alt", "title", "width", "height", "color", "target"].includes(n);
        if (!(okHref || okSrc || okStyle || okPlain)) child.removeAttribute(attr.name);
      }
      walk(child);
    }
  };
  walk(root);
  return root.innerHTML;
}
